import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useDismissable } from '../../lib/useDismissable';
import { NavBadge, NavItemLabel } from './NavBadge';
import { activeNavTo, allNav, barNav, moreNavGroups, type NavBadges, type NavItem } from './nav';
import { useShellMenu } from './useShellMenu';

/**
 * The bar's height over the safe area: 8px above a 56px row. The More sheet and its dimmed layer stop
 * here, so the bar stays lit and More can close the sheet again.
 */
const ABOVE_BAR = 'bottom-[calc(4rem+env(safe-area-inset-bottom))]';

/** A tab's colours: the brand green while current (in the shade that keeps 4.5:1 on the bar), else muted. */
const tabTone = (active: boolean) =>
  active ? 'font-semibold text-success dark:text-success-dark' : 'font-medium text-ink-muted';

const TAB =
  'focus-ring flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-[0.1875rem] rounded-lg text-[0.6875rem] leading-tight';

function BarTab({
  item,
  active,
  disabled,
  count,
}: {
  item: NavItem;
  active: boolean;
  disabled: boolean;
  count: number;
}) {
  const Icon = item.icon;
  const body = (
    <>
      <span className="relative">
        <Icon className="h-6 w-6" strokeWidth={2} aria-hidden />
        {item.badge && (
          <NavBadge kind={item.badge} count={count} className="absolute -right-2.5 -top-1.5" />
        )}
      </span>
      <NavItemLabel item={item} count={count} short className="max-w-full truncate px-0.5" />
    </>
  );
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        tabIndex={-1}
        className={cn(TAB, 'pointer-events-none font-medium text-ink-ghost')}
      >
        {body}
      </span>
    );
  }
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(TAB, tabTone(active))}
    >
      {body}
    </Link>
  );
}

/** One tile of the More sheet: its icon over its full label, with its badge at the top right. */
function MoreTile({
  item,
  active,
  count,
  onPick,
}: {
  item: NavItem;
  active: boolean;
  count: number;
  onPick: () => void;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onPick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'focus-ring relative flex min-h-[5.25rem] flex-col items-center justify-center gap-1.5 rounded-xl border px-1.5 py-2 text-center text-[0.8125rem] leading-tight transition-colors',
        active
          ? 'border-success bg-tint font-semibold text-success dark:border-success-dark dark:text-success-dark'
          : 'border-line font-medium text-ink hover:bg-hover',
      )}
    >
      <Icon className="h-6 w-6" strokeWidth={2} aria-hidden />
      <NavItemLabel item={item} count={count} />
      {item.badge && (
        <NavBadge kind={item.badge} count={count} className="absolute right-1.5 top-1.5" />
      )}
    </Link>
  );
}

/** How far down (in CSS px) the sheet must be dragged to close rather than spring back. */
const DRAG_TO_CLOSE = 64;

/**
 * The More tab and its sheet: the items the bar has no room for, as tiles in their two sidebar groups.
 * The sheet rises from the bar (which stays lit) over a dimmed page, and closes on the dimmed layer, a
 * drag down, Escape, More again, or a tile. While it is open, Tab stays inside it; the page behind it
 * cannot be scrolled, as the dimmed layer takes every press and swipe meant for it.
 */
function MoreMenu({ badges, activeTo }: { badges: NavBadges; activeTo: string | null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const drag = useRef<{ from: number; by: number } | null>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismissable(open, ref, close);
  useShellMenu(open, close, ref);

  const inMore = moreNavGroups.some((g) => g.items.some((item) => item.to === activeTo));
  const active = open || inMore;

  const onTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !sheet.current) return;
    const stops = [
      ...sheet.current.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)'),
    ];
    const [first, last] = [stops[0], stops[stops.length - 1]];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Drag the handle down to close. The sheet follows the finger (down only) while it moves; it is set on
  // the element directly, as a pointer's travel is in CSS px and needs no re-render.
  const onDragStart = (e: PointerEvent<HTMLDivElement>) => {
    drag.current = { from: e.clientY, by: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !sheet.current) return;
    drag.current.by = Math.max(0, e.clientY - drag.current.from);
    sheet.current.style.transform = `translateY(${drag.current.by}px)`;
  };
  const onDragEnd = () => {
    if (!drag.current) return;
    const far = drag.current.by > DRAG_TO_CLOSE;
    drag.current = null;
    if (sheet.current) sheet.current.style.transform = '';
    if (far) close();
  };

  return (
    <div ref={ref} className="flex min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={badges.sources > 0 ? 'More, Digest Sources need attention' : 'More'}
        className={cn(TAB, tabTone(active))}
      >
        <span className="relative">
          <MoreHorizontal className="h-6 w-6" strokeWidth={2} aria-hidden />
          {badges.sources > 0 && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-warning-dark ring-2 ring-card"
            />
          )}
        </span>
        <span aria-hidden>More</span>
      </button>

      {open && (
        <>
          {/* Inside this box, where useDismissable does not see a press on it: so it closes the sheet itself. */}
          <div
            aria-hidden
            onMouseDown={close}
            className={cn(
              'fixed inset-x-0 top-16 z-[5] bg-black/40 motion-safe:animate-fade-in',
              ABOVE_BAR,
            )}
          />
          <div
            ref={sheet}
            role="dialog"
            aria-modal="true"
            aria-label="More"
            onKeyDown={onTab}
            className={cn(
              'fixed inset-x-0 z-10 flex max-h-[80dvh] flex-col rounded-t-[1.25rem] border-t border-line bg-card shadow-panel motion-safe:animate-sheet-in',
              ABOVE_BAR,
            )}
          >
            <div
              aria-hidden
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              className="flex shrink-0 cursor-grab touch-none justify-center pb-2 pt-3"
            >
              <span className="h-1 w-10 rounded-full bg-line-input" />
            </div>
            <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-4">
              {moreNavGroups.map((group) => (
                <section key={group.label} aria-label={group.label} className="pt-2 first:pt-0">
                  <h2 className="pb-2 pt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-ink-muted">
                    {group.label}
                  </h2>
                  <div className="grid grid-cols-3 gap-2">
                    {group.items.map((item) => (
                      <MoreTile
                        key={item.to}
                        item={item}
                        active={item.to === activeTo}
                        count={item.badge ? badges[item.badge] : 0}
                        onPick={close}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The phone navigation (below `md`): a tab bar along the bottom with Dashboard, Campaigns & Posts, New
 * Content (`create`, raised in the middle), Messages, and More, which opens a sheet of everything else.
 * No sidebar and no hamburger. It is a row of the shell's column, under the page, so it never covers
 * any of it; its bottom padding clears the iPhone home indicator.
 *
 * `z-[45]`: over page content (which stacks up to z-40) so New Content's menu, the More sheet and their
 * dimmed layers cover it; under the top bar, whose own menus dim this.
 */
export function BottomNav({
  disabled = false,
  create,
  badges,
}: {
  disabled?: boolean;
  create?: ReactNode;
  badges: NavBadges;
}) {
  const { pathname, search } = useLocation();
  const activeTo = activeNavTo(allNav, pathname, search);
  const tab = (item: NavItem) => (
    <BarTab
      key={item.to}
      item={item}
      active={item.to === activeTo}
      disabled={disabled}
      count={item.badge ? badges[item.badge] : 0}
    />
  );
  const [first, second, ...rest] = barNav;

  return (
    <nav
      aria-label="Primary"
      aria-disabled={disabled || undefined}
      className="relative z-[45] flex shrink-0 items-start border-t border-line bg-card px-1 pb-[env(safe-area-inset-bottom)] pt-2 md:hidden"
    >
      {tab(first)}
      {tab(second)}
      <div className="flex min-w-0 flex-1 justify-center">{create}</div>
      {rest.map(tab)}
      {disabled ? (
        <span
          aria-disabled="true"
          className={cn(TAB, 'pointer-events-none font-medium text-ink-ghost')}
        >
          <MoreHorizontal className="h-6 w-6" strokeWidth={2} aria-hidden />
          More
        </span>
      ) : (
        <MoreMenu badges={badges} activeTo={activeTo} />
      )}
    </nav>
  );
}
